import { supabase } from './supabase';
import { CreditAnalyticsService } from './credit-analytics';

// Type definitions for credit purchase system
export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  discount_percentage: number | null;
  is_active: boolean | null;
  is_bonus_package: boolean | null;
  stripe_price_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  price_per_credit: number | null;
  description: string | null;
  bonus_credits?: number; // Optional for UI compatibility
}

export interface PurchaseOptions {
  packageId: string;
  paymentMethodId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface PurchaseResult {
  success: boolean;
  transactionId?: string;
  newCreditBalance?: number;
  error?: string;
  clientSecret?: string; // For Stripe payment confirmation
  checkoutUrl?: string; // For Stripe checkout redirect
}

export interface CreditRefund {
  transactionId: string;
  amount: number;
  reason: string;
  refundId?: string;
}


export class CreditPurchaseService {
  
  /**
   * Get all available credit packages
   */
  static async getCreditPackages(): Promise<CreditPackage[]> {
    try {
      const { data: packages, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('credits', { ascending: true });

      if (error) {
        throw error;
      }

      return packages || [];
    } catch (error) {
      console.error('Get credit packages error:', error);
      throw error;
    }
  }

  /**
   * Get specific credit package by ID
   */
  static async getCreditPackage(packageId: string): Promise<CreditPackage | null> {
    try {
      const { data: package_, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('id', packageId)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // No package found
        }
        throw error;
      }

      return package_;
    } catch (error) {
      console.error('Get credit package error:', error);
      throw error;
    }
  }

  /**
   * Purchase credit package using Stripe MCP
   */
  static async purchaseCreditPackage(options: PurchaseOptions): Promise<PurchaseResult> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      // Get the credit package
      const creditPackage = await this.getCreditPackage(options.packageId);
      if (!creditPackage) {
        return {
          success: false,
          error: 'Credit package not found'
        };
      }


      // Create Stripe checkout session or payment intent
      const stripeResult = await this.createStripePayment(creditPackage, user.id, options);
      
      if (!stripeResult.success) {
        return {
          success: false,
          error: stripeResult.error || 'Payment creation failed'
        };
      }

      // For immediate payments (like Apple Pay, Google Pay), process the credit addition
      if (stripeResult.paymentCompleted) {
        const newBalance = await this.processCreditAddition(
          user.id,
          creditPackage,
          stripeResult.paymentIntentId!
        );

        return {
          success: true,
          transactionId: stripeResult.transactionId,
          newCreditBalance: newBalance
        };
      }

      // For checkout sessions, return the URL
      return {
        success: true,
        checkoutUrl: stripeResult.checkoutUrl,
        clientSecret: stripeResult.clientSecret
      };

    } catch (error) {
      console.error('Purchase credit package error:', error);
      return {
        success: false,
        error: 'Failed to process credit purchase'
      };
    }
  }

  /**
   * Create Stripe payment (checkout session or payment intent)
   */
  private static async createStripePayment(
    creditPackage: CreditPackage,
    userId: string,
    options: PurchaseOptions
  ): Promise<{
    success: boolean;
    error?: string;
    checkoutUrl?: string;
    clientSecret?: string;
    paymentIntentId?: string;
    transactionId?: string;
    paymentCompleted?: boolean;
  }> {
    try {
      // For credit packs, we always use checkout sessions (one-time payments)
      // No direct payment with saved payment methods for now
      const checkoutSession = await this.createStripeCheckoutSession(creditPackage, userId, options);
      
      return {
        success: true,
        checkoutUrl: checkoutSession.url,
        transactionId: checkoutSession.id // Use session ID as transaction ID
      };
    } catch (error) {
      console.error('Create Stripe payment error:', error);
      return {
        success: false,
        error: 'Failed to create payment session'
      };
    }
  }


  /**
   * Create Stripe Checkout Session
   */
  private static async createStripeCheckoutSession(
    creditPackage: CreditPackage,
    userId: string,
    options: PurchaseOptions
  ): Promise<any> {
    try {
      // Get user email for Stripe checkout
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('email')
        .eq('id', userId)
        .single();

      if (userError) {
        throw new Error('Failed to get user email');
      }

      // Create checkout session using the Edge Function
      const { data, error } = await supabase.functions.invoke('stripe-checkout-v2', {
        body: {
          action: 'create_checkout_session',
          params: {
            success_url: options.successUrl || `${window.location.origin}/dashboard/credits?purchase_success=true&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: options.cancelUrl || `${window.location.origin}/dashboard/credits?purchase_cancelled=true`,
            mode: 'payment', // One-time payment for credit packs
            line_items: [
              {
                price: creditPackage.stripe_price_id,
                quantity: 1,
              },
            ],
            metadata: {
              user_id: userId,
              package_id: creditPackage.id,
              package_name: creditPackage.name,
              credits: creditPackage.credits.toString(),
              type: 'credit_purchase'
            },
            customer_email: userData.email
          }
        }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(`Failed to create checkout session: ${error.message}`);
      }

      if (!data) {
        console.error('No response from Stripe Edge Function');
        throw new Error('Invalid response from Stripe');
      }

      // Handle different response formats from Edge Function
      let sessionId: string;
      let sessionUrl: string;

      if (data.id && data.url) {
        // New format: { id, url }
        sessionId = data.id;
        sessionUrl = data.url;
      } else if (data.sessionId && data.sessionUrl) {
        // Legacy format: { sessionId, sessionUrl }
        sessionId = data.sessionId;
        sessionUrl = data.sessionUrl;
      } else {
        console.error('Invalid Stripe response format:', data);
        throw new Error('Invalid response from Stripe: missing session URL or ID');
      }

      return {
        id: sessionId,
        url: sessionUrl
      };
    } catch (error) {
      console.error('Create Stripe checkout session error:', error);
      throw error;
    }
  }

  /**
   * Process credit addition after successful payment
   */
  static async processCreditAddition(
    userId: string,
    creditPackage: CreditPackage,
    paymentIntentId: string
  ): Promise<number> {
    try {
      // Get current user credit balance
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('credits')
        .eq('id', userId)
        .single();

      if (userError) {
        throw userError;
      }

      const currentBalance = userData.credits || 0;
      const newBalance = currentBalance + creditPackage.credits;

      // Update user credits
      const { error: updateError } = await supabase
        .from('users')
        .update({ credits: newBalance })
        .eq('id', userId);

      if (updateError) {
        throw updateError;
      }

      // Record transaction in credit_transactions table
      await CreditAnalyticsService.recordCreditTransaction(
        creditPackage.credits,
        'purchase',
        `Purchased ${creditPackage.name} - ${creditPackage.credits} credits`,
        {
          packageId: creditPackage.id,
          packageName: creditPackage.name,
          priceCents: creditPackage.price_cents,
          discountPercentage: creditPackage.discount_percentage
        },
        undefined, // No analysis ID
        undefined, // No subscription ID
        paymentIntentId
      );

      return newBalance;
    } catch (error) {
      console.error('Process credit addition error:', error);
      throw error;
    }
  }

  /**
   * Process credit refund
   */
  static async processCreditRefund(refund: CreditRefund): Promise<{
    success: boolean;
    newBalance?: number;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      // Get original transaction
      const { data: transaction, error: transactionError } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('id', refund.transactionId)
        .eq('user_id', user.id)
        .single();

      if (transactionError) {
        return {
          success: false,
          error: 'Transaction not found'
        };
      }

      // Validate refund amount
      if (refund.amount > transaction.amount) {
        return {
          success: false,
          error: 'Refund amount exceeds original transaction amount'
        };
      }

      // Get current balance
      const { data: userData, error: userError2 } = await supabase
        .from('users')
        .select('credits')
        .eq('id', user.id)
        .single();

      if (userError2) {
        throw userError2;
      }

      const currentBalance = userData.credits || 0;
      
      // Check if user has enough credits to refund
      if (currentBalance < refund.amount) {
        return {
          success: false,
          error: 'Insufficient credits for refund'
        };
      }

      const newBalance = currentBalance - refund.amount;

      // Update user credits
      const { error: updateError } = await supabase
        .from('users')
        .update({ credits: newBalance })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Record refund transaction
      await CreditAnalyticsService.recordCreditTransaction(
        -refund.amount,
        'refund',
        `Refund: ${refund.reason}`,
        {
          originalTransactionId: refund.transactionId,
          refundId: refund.refundId,
          refundReason: refund.reason
        },
        undefined,
        undefined,
        refund.refundId
      );

      // Process Stripe refund if refundId provided
      if (refund.refundId) {
        await this.processStripeRefund(refund.refundId, refund.amount);
      }

      return {
        success: true,
        newBalance
      };
    } catch (error) {
      console.error('Process credit refund error:', error);
      return {
        success: false,
        error: 'Failed to process refund'
      };
    }
  }

  /**
   * Process Stripe refund
   */
  private static async processStripeRefund(refundId: string, amount: number): Promise<void> {
    try {
      // This would use Stripe MCP to process the refund
      // Implementation would be added when Stripe MCP is available
      console.log(`Processing Stripe refund: ${refundId} for amount: ${amount}`);
    } catch (error) {
      console.error('Process Stripe refund error:', error);
      // Don't throw - log the error but continue with credit refund
    }
  }

  /**
   * Get recommended credit package based on usage patterns
   */
  static async getRecommendedPackage(): Promise<CreditPackage | null> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's usage analytics
      const analytics = await CreditAnalyticsService.getCreditUsageAnalytics(user.id);
      const packages = await this.getCreditPackages();

      if (packages.length === 0) {
        return null;
      }

      // Simple recommendation logic based on monthly usage
      const monthlyUsage = analytics.usageHistory.totalCreditsUsed;
      
      // Recommend package with 20% buffer over monthly usage
      const recommendedCredits = Math.ceil(monthlyUsage * 1.2);
      
      // Find the package closest to recommended credits
      let bestPackage = packages[0];
      let smallestDiff = Math.abs(packages[0].credits - recommendedCredits);

      for (const pkg of packages) {
        const diff = Math.abs(pkg.credits - recommendedCredits);
        if (diff < smallestDiff && pkg.credits >= recommendedCredits) {
          bestPackage = pkg;
          smallestDiff = diff;
        }
      }

      return bestPackage;
    } catch (error) {
      console.error('Get recommended package error:', error);
      return null;
    }
  }


  /**
   * Get credit purchase history
   */
  static async getPurchaseHistory(): Promise<any[]> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data: transactions, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('transaction_type', 'purchase')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return transactions || [];
    } catch (error) {
      console.error('Get purchase history error:', error);
      throw error;
    }
  }

  /**
   * Calculate effective price per credit for packages
   */
  static calculatePricePerCredit(creditPackage: CreditPackage): number {
    // Price is already the final price, discount is just for display
    return creditPackage.price_cents / creditPackage.credits;
  }

  /**
   * Get best value package (lowest price per credit)
   */
  static async getBestValuePackage(): Promise<CreditPackage | null> {
    try {
      const packages = await this.getCreditPackages();
      
      if (packages.length === 0) {
        return null;
      }

      let bestPackage = packages[0];
      let lowestPricePerCredit = this.calculatePricePerCredit(packages[0]);

      for (const pkg of packages) {
        const pricePerCredit = this.calculatePricePerCredit(pkg);
        if (pricePerCredit < lowestPricePerCredit) {
          bestPackage = pkg;
          lowestPricePerCredit = pricePerCredit;
        }
      }

      return bestPackage;
    } catch (error) {
      console.error('Get best value package error:', error);
      return null;
    }
  }

  /**
   * Verify checkout session status
   */
  static async verifyCheckoutSession(sessionId: string): Promise<{
    success: boolean;
    paymentCompleted: boolean;
    creditsPurchased: number;
    error?: string;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-checkout-v2', {
        body: {
          action: 'verify_session_status',
          params: {
            session_id: sessionId
          }
        }
      });

      if (error) {
        console.error('Session verification error:', error);
        return {
          success: false,
          paymentCompleted: false,
          creditsPurchased: 0,
          error: error.message
        };
      }

      return {
        success: true,
        paymentCompleted: data.payment_completed || false,
        creditsPurchased: data.credits_purchased || 0
      };
    } catch (error) {
      console.error('Verify checkout session error:', error);
      return {
        success: false,
        paymentCompleted: false,
        creditsPurchased: 0,
        error: 'Failed to verify session status'
      };
    }
  }
}

export default CreditPurchaseService; 